import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inventoryService } from '../../services/inventoryService';

const initialState = {
  vehicles: [],
  currentVehicle: null,
  serviceAppointments: [],
  serviceHistory: [],
  stats: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

// Vehicle inventory thunks
export const fetchVehicles = createAsyncThunk(
  'inventory/fetchVehicles',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await inventoryService.getVehicles(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch vehicles');
    }
  }
);

export const fetchVehicle = createAsyncThunk(
  'inventory/fetchVehicle',
  async (id, { rejectWithValue }) => {
    try {
      const data = await inventoryService.getVehicle(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch vehicle');
    }
  }
);

export const createVehicle = createAsyncThunk(
  'inventory/createVehicle',
  async (vehicleData, { rejectWithValue }) => {
    try {
      const data = await inventoryService.createVehicle(vehicleData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create vehicle');
    }
  }
);

export const updateVehicle = createAsyncThunk(
  'inventory/updateVehicle',
  async ({ id, vehicleData }, { rejectWithValue }) => {
    try {
      const data = await inventoryService.updateVehicle(id, vehicleData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update vehicle');
    }
  }
);

export const deleteVehicle = createAsyncThunk(
  'inventory/deleteVehicle',
  async (id, { rejectWithValue }) => {
    try {
      await inventoryService.deleteVehicle(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete vehicle');
    }
  }
);

// Service appointment thunks
export const fetchServiceAppointments = createAsyncThunk(
  'inventory/fetchServiceAppointments',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await inventoryService.getServiceAppointments(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch service appointments');
    }
  }
);

export const createServiceAppointment = createAsyncThunk(
  'inventory/createServiceAppointment',
  async (appointmentData, { rejectWithValue }) => {
    try {
      const data = await inventoryService.createServiceAppointment(appointmentData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create service appointment');
    }
  }
);

export const updateServiceAppointment = createAsyncThunk(
  'inventory/updateServiceAppointment',
  async ({ id, appointmentData }, { rejectWithValue }) => {
    try {
      const data = await inventoryService.updateServiceAppointment(id, appointmentData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update service appointment');
    }
  }
);

// Service history
export const fetchServiceHistory = createAsyncThunk(
  'inventory/fetchServiceHistory',
  async (vehicleId, { rejectWithValue }) => {
    try {
      const data = await inventoryService.getServiceHistory(vehicleId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch service history');
    }
  }
);

// Stats
export const fetchInventoryStats = createAsyncThunk(
  'inventory/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await inventoryService.getInventoryStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch inventory stats');
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentVehicle: (state) => {
      state.currentVehicle = null;
      state.serviceHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vehicles
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || action.payload.length,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Single vehicle
      .addCase(fetchVehicle.fulfilled, (state, action) => {
        state.currentVehicle = action.payload;
      })
      // Create vehicle
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.vehicles.unshift(action.payload);
      })
      // Update vehicle
      .addCase(updateVehicle.fulfilled, (state, action) => {
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle?.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      // Delete vehicle
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.vehicles = state.vehicles.filter(vehicle => vehicle.id !== action.payload);
      })
      // Service appointments
      .addCase(fetchServiceAppointments.fulfilled, (state, action) => {
        state.serviceAppointments = action.payload.results || action.payload;
      })
      .addCase(createServiceAppointment.fulfilled, (state, action) => {
        state.serviceAppointments.unshift(action.payload);
      })
      .addCase(updateServiceAppointment.fulfilled, (state, action) => {
        const index = state.serviceAppointments.findIndex(appt => appt.id === action.payload.id);
        if (index !== -1) {
          state.serviceAppointments[index] = action.payload;
        }
      })
      // Service history
      .addCase(fetchServiceHistory.fulfilled, (state, action) => {
        state.serviceHistory = action.payload;
      })
      // Stats
      .addCase(fetchInventoryStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearError, clearCurrentVehicle } = inventorySlice.actions;
export default inventorySlice.reducer;